import type {
  TrackerMotionDurations,
  TrackerMotionEasings,
  TrackerMotionSprings,
  TrackerMotionTransitions,
} from '@/lib/motion/presets.types'

/**
 * The tracker's own motion tokens. tracker-ui cannot import from `client`, so
 * the shared curves are duplicated here rather than imported. Some tokens
 * mirror the client's values; others are tracker-only and marked as such.
 * See docs/frontend-design.md for which is which.
 *
 * tracker-ui does NOT mount its own `<MotionConfig>` — it is a
 * shell-injected library (see `packages/tracker-ui` in the root
 * `pnpm-workspace.yaml` docs), so each consuming shell (`client`,
 * `apps/tracker-web`) is responsible for mounting
 * `<MotionConfig reducedMotion="user">` at its own app root.
 */

export const easings: TrackerMotionEasings = {
  entrance: [0.16, 1, 0.3, 1],
  exit: [0.42, 0, 1, 1],
}

export const durations: TrackerMotionDurations = {
  fast: 0.12,
  normal: 0.18,
  panel: 0.24,
  collapse: 0.2,
  itemStagger: 0.04,
  attention: 0.9,
}

export const transitions: TrackerMotionTransitions = {
  dialog: {
    open: { duration: durations.normal, ease: easings.entrance },
    close: { duration: durations.fast, ease: easings.exit },
  },

  panel: { duration: durations.panel, ease: easings.entrance },

  // Matches `panel` today but owns the accordion, so either can retune alone.
  // Open outlasts `collapse` so a long answer does not snap into view.
  expand: { duration: durations.panel, ease: easings.entrance },

  collapse: { duration: durations.collapse, ease: 'easeOut' },

  // A tween, not a spring: a mode switch reads as a state change, not a bounce.
  modeIndicator: { duration: durations.normal, ease: easings.entrance },

  // Rises fast, then holds and fades, so the reader's eye lands before it goes.
  attention: {
    duration: durations.attention,
    ease: easings.entrance,
    times: [0, 0.2, 1],
  },
}

// switchThumb mirrors the client at identical values; listReorder does not.
export const springs: TrackerMotionSprings = {
  switchThumb: { type: 'spring', stiffness: 500, damping: 30 },
  // Tracker-only, so this one is not mirrored from the client.
  listReorder: { type: 'spring', stiffness: 420, damping: 34 },
}
